import { createFileRoute, redirect } from "@tanstack/react-router";
import { z } from "zod";
import { Role } from "@/core/components/auth/auth.types";
import { ErrorComponent } from "@/core/components/ErrorComponent";
import { LoadingList } from "@/core/components/LoadingList";
import {
	commissionsDetailQueryOptions,
	commissionsSummaryQueryOptions,
	defaultCommissionsSearch,
} from "@/core/components/sites/group/commissions/commissionsReportQueryOptions";
import { getGroupsQueryOptions } from "@/core/components/sites/propertyQueryOptions";
import { normalizeReportRange } from "@/core/lib/reportDateRange";

const commissionsSearchSchema = z.object({
	dateFrom: z.string().catch(""),
	dateTo: z.string().catch(""),
	tab: z.enum(["summary", "detail"]).catch("summary"),
	page: z.number().catch(1),
	pageSize: z.number().catch(10),
	propertyId: z.string().catch(""),
	bookingId: z.string().catch(""),
	purchaseId: z.string().catch(""),
});

export const Route = createFileRoute(
	"/_layout/dashboard/group/$groupId/commissions/",
)({
	validateSearch: (search) => {
		const parsed = commissionsSearchSchema.parse(search);
		return {
			...parsed,
			...normalizeReportRange(parsed, defaultCommissionsSearch()),
		};
	},
	beforeLoad: ({ context: { auth } }) => {
		if (auth.isExpired()) {
			return;
		}
		if (!auth.isAuthenticated) {
			throw redirect({
				to: "/",
			});
		}
		if (
			auth.user?.role === Role["Front-Desk"] ||
			auth.user?.role === Role.Editor ||
			auth.user?.role === Role.Blogger
		) {
			throw redirect({
				to: "/dashboard",
			});
		}
	},
	loaderDeps: ({ search }) => ({
		dateFrom: search.dateFrom,
		dateTo: search.dateTo,
		tab: search.tab,
		page: search.page,
		pageSize: search.pageSize,
		propertyId: search.propertyId,
		bookingId: search.bookingId,
		purchaseId: search.purchaseId,
	}),
	loader: ({ context: { queryClient, auth }, params: { groupId }, deps }) => {
		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		queryClient.ensureQueryData(getGroupsQueryOptions(auth.token!));
		const query = {
			token: auth.token!,
			groupId,
			dateFrom: deps.dateFrom,
			dateTo: deps.dateTo,
			page: deps.page,
			limit: deps.pageSize,
		};
		if (deps.tab === "detail") {
			// eslint-disable-next-line @typescript-eslint/no-floating-promises
			queryClient.ensureQueryData(
				commissionsDetailQueryOptions({
					...query,
					propertyId: deps.propertyId,
					bookingId: deps.bookingId,
					purchaseId: deps.purchaseId,
				}),
			);
		} else {
			// eslint-disable-next-line @typescript-eslint/no-floating-promises
			queryClient.ensureQueryData(commissionsSummaryQueryOptions(query));
		}
	},
	pendingComponent: LoadingList,
	errorComponent: ErrorComponent,
});
